<template>
  <nav class="flex flex-col gap-2" :aria-label="t('nav.main_menu')">
    <RouterLink
      to="/"
      class="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-toned transition-colors hover:bg-elevated"
      active-class=""
      exact-active-class="bg-elevated text-highlighted ring-1 ring-default"
      @click="emit('navigate')"
    >
      <UIcon name="i-lucide-house" class="size-4.5" />
      {{ t('nav.home') }}
    </RouterLink>

    <section v-for="mod in modules" :key="mod.key" class="rounded-xl border p-1.5" :class="mod.tone.tile">
      <div class="flex items-center gap-2.5 px-1.5 py-1">
        <span class="flex size-8 flex-none items-center justify-center rounded-lg" :class="mod.tone.icon">
          <UIcon :name="mod.icon" class="size-4.5" />
        </span>
        <div class="min-w-0 leading-tight">
          <div class="text-sm font-bold text-highlighted">{{ mod.title }}</div>
          <div class="truncate text-[11px] text-muted">{{ mod.subtitle }}</div>
        </div>
      </div>

      <RouterLink
        v-for="item in mod.items"
        :key="item.to"
        :to="item.to"
        class="mt-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors"
        :class="[mod.tone.hover, item.primary ? [mod.tone.strong, 'font-bold text-highlighted'] : 'font-medium text-toned']"
        :active-class="mod.tone.active"
        @click="emit('navigate')"
      >
        <UIcon :name="item.icon" class="size-4" />
        {{ item.label }}
      </RouterLink>
    </section>

    <div class="px-3 pt-1 text-[11px] font-semibold tracking-wide text-muted uppercase">{{ t('modules.common') }}</div>

    <RouterLink
      v-for="item in commonItems"
      :key="item.to"
      :to="item.to"
      class="flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium text-toned transition-colors hover:bg-elevated"
      active-class="bg-elevated text-highlighted ring-1 ring-default"
      @click="emit('navigate')"
    >
      <UIcon :name="item.icon" class="size-4.5" />
      {{ item.label }}
    </RouterLink>
  </nav>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { isAdmin } from '@/libs/auth'

const emit = defineEmits<{ navigate: [] }>()

const { t } = useI18n()

// Full class strings (not built dynamically) so Tailwind can see every class.
const TONES = {
  primary: {
    tile: 'border-primary/30 bg-primary/10',
    icon: 'bg-primary text-on-primary',
    hover: 'hover:bg-primary/15',
    strong: 'bg-primary/20',
    active: 'bg-primary! text-on-primary! shadow-sm',
  },
  info: {
    tile: 'border-info/30 bg-info/10',
    icon: 'bg-info text-inverted',
    hover: 'hover:bg-info/15',
    strong: 'bg-info/20',
    active: 'bg-info! text-inverted! shadow-sm',
  },
}

const modules = computed(() => [
  {
    key: 'rocno',
    tone: TONES.primary,
    icon: 'i-lucide-package',
    title: t('modules.rocno'),
    subtitle: t('modules.rocno_sub'),
    items: [
      { to: '/rocno/nov', label: t('nav.entry'), icon: 'i-lucide-plus', primary: true },
      { to: '/rocno/pregled', label: t('nav.overview'), icon: 'i-lucide-bar-chart-3', primary: false },
      { to: '/rocno/evidenca', label: t('nav.log'), icon: 'i-lucide-clipboard-list', primary: false },
      { to: '/rocno/inventura', label: t('nav.stocktake'), icon: 'i-lucide-boxes', primary: false },
    ],
  },
  {
    key: 'linija',
    tone: TONES.info,
    icon: 'i-lucide-workflow',
    title: t('modules.linija'),
    subtitle: t('modules.linija_sub'),
    items: [
      { to: '/linija/nov', label: t('nav.entry'), icon: 'i-lucide-plus', primary: true },
      { to: '/linija/pregled', label: t('nav.overview'), icon: 'i-lucide-bar-chart-3', primary: false },
      { to: '/linija/evidenca', label: t('nav.log'), icon: 'i-lucide-list', primary: false },
    ],
  },
])

const commonItems = computed(() => [
  { to: '/navodila', label: t('nav.guide'), icon: 'i-lucide-book-open' },
  { to: '/sifranti', label: t('nav.registries'), icon: 'i-lucide-list-checks' },
  ...(isAdmin() ? [{ to: '/sledljivost', label: t('nav.audit'), icon: 'i-lucide-history' }] : []),
])
</script>
